/**
 * Deterministic RNG.
 *
 * Every random decision in the simulation comes from here so a run can be
 * replayed exactly — on another machine, or on the leaderboard server when it
 * verifies a submitted score. mulberry32 is small, fast and has a 32-bit state
 * we can carry around in the sim state.
 */
export class Rng {
  private s: number

  constructor(seed: number) {
    this.s = seed >>> 0
  }

  /** Float in [0, 1). */
  next(): number {
    this.s = (this.s + 0x6d2b79f5) >>> 0
    let t = this.s
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }

  /** Integer in [0, n). */
  int(n: number): number {
    return Math.floor(this.next() * n) % Math.max(1, n)
  }

  /** Float in [lo, hi). */
  range(lo: number, hi: number): number {
    return lo + this.next() * (hi - lo)
  }

  pick<T>(items: readonly T[]): T {
    return items[this.int(items.length)]
  }

  clone(): Rng {
    const r = new Rng(0)
    r.s = this.s
    return r
  }
}

/** FNV-1a — used to turn a date string into a daily seed everyone shares. */
export function hashString(str: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

/** UTC calendar day, e.g. "2026-08-09". The daily challenge key. */
export function utcDayKey(now = new Date()): string {
  return now.toISOString().slice(0, 10)
}

export function dailySeed(dayKey: string): number {
  return hashString(`ouroboros:${dayKey}`)
}
